/**
 * How the query client meets failure (wired in `main.tsx`).
 *
 * - An API restart (no answer, 502, 503, 504, a proxy's bare 500) is retried
 *   quietly for about twenty seconds before anything is said.
 * - A refusal (401, 403, 404, 422…) is not retried: asking again gets the same
 *   answer, and the page explains it.
 * - Anything else is retried twice, as before.
 * - A refresh that still fails while the page shows earlier data raises one
 *   toast with Retry, rather than leaving that data looking current. A first
 *   load that fails is the page's to explain; a query opts in to the toast for
 *   that with `meta: { errorToast: true }`, or out of it altogether with
 *   `meta: { errorToast: false }`.
 */
import type { Query, QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { isRefusal, isTransientError } from "./classify";
import { errorCopyNow } from "./copy";

/** Retries for a transient failure: 1 + 2 + 4 + 8 + 8 s, about 23 s in all. */
const TRANSIENT_RETRIES = 5;
const OTHER_RETRIES = 2;
const TOAST_ID = "query-refresh-failed";
/** After a person closes the toast, how long further failures stay quiet. */
const SNOOZE_MS = 60_000;

let snoozedUntil = 0;

export function retryQuery(failureCount: number, error: unknown): boolean {
  if (isRefusal(error)) return false;
  return failureCount < (isTransientError(error) ? TRANSIENT_RETRIES : OTHER_RETRIES);
}

export function retryQueryDelay(failureCount: number, error: unknown): number {
  const retryAfter = (error as { retryAfter?: unknown } | null)?.retryAfter;
  if (typeof retryAfter === "number" && retryAfter > 0) return Math.min(retryAfter * 1000, 30_000);
  return Math.min(1000 * 2 ** failureCount, isTransientError(error) ? 8_000 : 30_000);
}

function errorToast(query: Query<unknown, unknown, unknown, readonly unknown[]>): boolean | undefined {
  const value = (query.meta as { errorToast?: unknown } | undefined)?.errorToast;
  return typeof value === "boolean" ? value : undefined;
}

/** The query cache's `onError`, after retries are spent. */
export function announceQueryFailure(
  client: QueryClient,
  error: unknown,
  query: Query<unknown, unknown, unknown, readonly unknown[]>,
): void {
  const optIn = errorToast(query);
  if (optIn === false || query.queryKey[0] === "session") return;
  if (isRefusal(error)) return;
  // Nobody is looking at it (a prefetch, a page already left).
  if (query.getObserversCount() === 0) return;
  if (query.state.data === undefined && optIn !== true) return;
  if (Date.now() < snoozedUntil) return;

  const copy = errorCopyNow();
  toast.warning(copy.toastTitle, {
    id: TOAST_ID,
    description: copy.toastBody,
    duration: 10_000,
    action: {
      label: copy.retry,
      onClick: () => {
        void client.refetchQueries({ type: "active", predicate: (q) => q.state.status === "error" });
      },
    },
    onDismiss: () => {
      snoozedUntil = Date.now() + SNOOZE_MS;
    },
  });
}
